import { Directive, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, TemplateRef, ViewContainerRef, inject } from '@angular/core';
import { Subscription, filter } from 'rxjs';
import { FeatureToggleService } from '../../services/feature-toggle.service';
import { AppFacadeService } from '../../services/app-facade.service';
import { ACCOUNT_FEATURE_PREFIX } from '../../enums/user-account-feature.enum';

@Directive({
    standalone: false,
    selector: '[porFeatureAvailability]'
})
export class AvailabilityDirective implements OnChanges, OnInit, OnDestroy {
    private readonly templateRef = inject<TemplateRef<any>>(TemplateRef);
    private readonly viewContainer = inject(ViewContainerRef);
    private readonly featureToggleService = inject(FeatureToggleService);
    private readonly appFacadeService = inject(AppFacadeService);
    private accountFeaturesSubscription?: Subscription;

    @Input() featureToken?: string[] = [];
    @Input() options?: { subFeature: string; state: string; ignoreFeatureCheck?: boolean };
    @Input() fallbackTemplate?: TemplateRef<any>;

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['featureToken'] || changes['options']) {
            this.checkAvailability();
        }
    }

    // ADO-114494: Account.* gates now fail closed, but the map loads asynchronously and this
    // directive otherwise evaluates once against an empty map. Re-evaluate when the map resolves
    // so a denied-then-granted capability recovers instead of latching false for the session.
    ngOnInit(): void {
        if (this.featureToken?.some(token => token.startsWith(ACCOUNT_FEATURE_PREFIX))) {
            this.accountFeaturesSubscription = this.appFacadeService
                .isAccountFeaturesLoaded()
                .pipe(filter(Boolean))
                .subscribe(() => this.checkAvailability());
        }
    }

    ngOnDestroy(): void {
        this.accountFeaturesSubscription?.unsubscribe();
    }

    private checkAvailability(): void {
        this.viewContainer.clear();

        // Check for availability with options if provided
        if (this.options && this.options.subFeature && this.options.state) {
            const isAnyFeatureAvailable = this.featureToken?.some(token => this.featureToggleService.isAvailable(token, this.options));

            if (isAnyFeatureAvailable) {
                this.viewContainer.createEmbeddedView(this.templateRef);
                return;
            }
        }

        // Fallback to checking availability with just the featureToken
        const isAnyFeatureAvailable = this.featureToken?.some(token => this.featureToggleService.isAvailable(token));

        if (isAnyFeatureAvailable) {
            this.viewContainer.createEmbeddedView(this.templateRef);
        } else if (this.fallbackTemplate) {
            this.viewContainer.createEmbeddedView(this.fallbackTemplate);
        } else if (!this.featureToken || this.featureToken.length === 0) {
            this.viewContainer.createEmbeddedView(this.templateRef);
        }
    }
}
